import useSWR from 'swr';
import { getSanityClient } from '../../lib/sanity/sanityClient';
import { Sanity$Document } from '../../types/sanityTypes';
import { Otovo$Locale } from '../../types/otovoweb';
import {
  LocaleToPathMap,
  LanguageSwitcherPathResolver,
  Sanity$TranslatedVariant,
} from './types';

function defaultPathResolver(locale, slug) {
  return slug;
}

function buildLocaleToPathMapForSanityPage(
  defaultLocale: Otovo$Locale,
  translatedVariants: Sanity$TranslatedVariant[],
  pathResolver,
): LocaleToPathMap {
  return translatedVariants.reduce((localeToPathMap, translatedVariant) => {
    const locale = translatedVariant.locale
      ? translatedVariant.locale
      : defaultLocale;
    const slug = translatedVariant.slug as unknown as string;
    return {
      ...localeToPathMap,
      [locale]: pathResolver(locale, slug),
    };
  }, {});
}

/**
 * Builds the LocaleToPathMap assuming that the page exists in all
 * market-relevant locales under the same (un-translated) path.
 */
function buildStaticLocaleToPathMap(
  marketRelevantLocales: Otovo$Locale[],
  currentPath: string,
): LocaleToPathMap {
  return marketRelevantLocales.reduce(
    (localeToPathMap, locale) => ({
      ...localeToPathMap,
      [locale]: currentPath,
    }),
    {},
  );
}

type LocaleToPathProps = {
  marketRelevantLocales: Otovo$Locale[];
  defaultLocale: Otovo$Locale;
  currentPath: string;
  sanityDocument?: Sanity$Document;
  pathResolver: LanguageSwitcherPathResolver;
};

export type LocaleToPathReturnType = {
  localeToPathMap: LocaleToPathMap | undefined;
  isLoading: boolean;
  error;
};

/**
 * Collect and provide the data that LanguageSwitcher needs to link alternate
 * language versions of a given page.
 */
export function useLocaleToPathMap({
  marketRelevantLocales,
  defaultLocale,
  currentPath,
  pathResolver = defaultPathResolver,
  sanityDocument,
}: LocaleToPathProps): LocaleToPathReturnType {
  const isCurrentPageFromSanity = !!sanityDocument;

  let sanityDocumentID: string | undefined;
  let documentType: string | undefined;
  if (isCurrentPageFromSanity) {
    // The page ID is provided in different places depending on whether the page
    // is the language version that was created first (sanityDocument._id) or a
    // translated version that was added later (sanityDocument.__i18n_base._ref).
    sanityDocumentID = sanityDocument.__i18n_base
      ? sanityDocument.__i18n_base._ref
      : sanityDocument._id;
    documentType = sanityDocument.documentType;
  }

  const groqQuery = `*[
    _type == '${documentType}'
    && _id match '${sanityDocumentID}'
    && defined(__i18n_lang)
    && defined(slug.current)
  ]{
    'locale': __i18n_lang,
    'slug': slug.current
  }`;
  const {
    data: sanityData,
    error,
    isLoading,
  } = useSWR(
    isCurrentPageFromSanity ? groqQuery : null,
    (query: string) => getSanityClient().fetch(query),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    },
  );

  if (isCurrentPageFromSanity) {
    if (error || !sanityData) {
      return { localeToPathMap: undefined, isLoading, error };
    }
    const localeToPathMap = buildLocaleToPathMapForSanityPage(
      defaultLocale,
      sanityData,
      pathResolver,
    );
    return { localeToPathMap, isLoading, error };
  }

  const localeToPathMap = buildStaticLocaleToPathMap(
    marketRelevantLocales,
    currentPath,
  );
  return { localeToPathMap, isLoading, error };
}
